import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetAdminQuizzesQuery, useGetAdminQuizDifficultiesQuery, useGetAdminQuizSourceMaterialDownloadUrlLazyQuery, useDeleteAdminQuizMutation, useActivateAdminQuizMutation } from "../../../lib/apollo/graphql/quiz/quiz-generated";
import PageWrapper from "../../common/components/PageWrapper";
import QuizSearchFilters from "../components/QuizSearchFilters";
import QuizTable from "../components/QuizTable";
import QuizPagination from "../components/QuizPagination";
import QuizBulkActions from "../components/QuizBulkActions";

export interface Quiz {
  quizId: string;
  quizNumber: number;
  status: "활성" | "비활성" | "신규" | "반려";
  title: string;
  type: "객관식" | "O/X" | "단답형";
  difficulty: string;
  points: number;
  createdAt: string;
  updatedAt: string;
  lastModifier: string;
  sourceMaterialId?: string;
  generationJobId?: string;
}

// Helper functions to map API data to UI data
const mapQuizStatus = (status: string): "활성" | "비활성" | "신규" | "반려" => {
  switch (status) {
    case "ACTIVE":
      return "활성";
    case "DELETED":
    case "ON_HOLD":
      return "비활성";
    case "REJECTED":
      return "반려";
    case "DRAFTED":
      return "신규";
    default:
      return "신규";
  }
};

const mapQuizType = (type: string): "객관식" | "O/X" | "단답형" => {
  switch (type) {
    case "MCQ":
      return "객관식";
    case "OX":
      return "O/X";
    case "SHORT":
      return "단답형";
    default:
      return "객관식";
  }
};

const mapTypeToAPI = (type: string): string | undefined => {
  if (!type) return undefined;
  switch (type) {
    case "객관식":
      return "MCQ";
    case "O/X":
      return "OX";
    case "단답형":
      return "SHORT";
    default:
      return undefined;
  }
};

// 날짜 변환 함수: 선택한 날짜를 API 형식으로 변환
// startDate: 선택한 날짜의 전날 15:00:00.000Z
// endDate: 선택한 날짜의 14:59:59.999Z
const convertDateForAPI = (dateString: string, isStartDate: boolean): string => {
  if (!dateString) return "";

  const date = new Date(dateString);

  if (isStartDate) {
    // 전날로 설정
    date.setDate(date.getDate() - 1);
    date.setUTCHours(15, 0, 0, 0);
  } else {
    // 같은 날 14:59:59.999로 설정
    date.setUTCHours(14, 59, 59, 999);
  }

  return date.toISOString();
};

const mapStatusToAPI = (status: string): string | undefined => {
  if (!status) return undefined;
  switch (status) {
    case "활성":
      return "ACTIVE";
    case "비활성":
      return "ON_HOLD";
    case "신규":
      return "DRAFTED";
    case "반려":
      return "REJECTED";
    default:
      return undefined;
  }
};

export default function QuizManagementContainer() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState(""); // This will store difficulty ID
  const [generationNumber, setGenerationNumber] = useState("");
  const [dateFieldFilter, setDateFieldFilter] = useState("createdAt");
  const [dateRange, setDateRange] = useState("전체");
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");

  // Fetch difficulties to populate dropdown
  const { data: difficultiesData } = useGetAdminQuizDifficultiesQuery({
    variables: { includeInactive: true },
    fetchPolicy: "network-only",
  });

  // Lazy query for downloading document
  const [getDownloadUrl] = useGetAdminQuizSourceMaterialDownloadUrlLazyQuery();

  // Mutations for bulk actions
  const [deleteQuiz] = useDeleteAdminQuizMutation();
  const [activateQuiz] = useActivateAdminQuizMutation();

  // Create difficulty options for dropdown
  const difficultyOptions = useMemo(() => {
    const options = [{ label: "전체난이도", value: "" }];

    if (difficultiesData?.difficulties) {
      const sortedDifficulties = [...difficultiesData.difficulties].sort((a, b) => a.order - b.order);
      sortedDifficulties.forEach(difficulty => {
        options.push({
          label: difficulty.label,
          value: difficulty.id,
        });
      });
    }

    return options;
  }, [difficultiesData]);

  // Initialize search from URL query parameters
  useEffect(() => {
    const urlSearchField = searchParams.get("searchField");
    const urlSearchQuery = searchParams.get("searchQuery");

    if (urlSearchField === "생성번호" && urlSearchQuery) {
      setGenerationNumber(urlSearchQuery);
    } else if (urlSearchQuery) {
      setSearchQuery(urlSearchQuery);
    }
  }, [searchParams]);

  // 날짜 범위 계산
  const getDateRange = (): { startDate?: string; endDate?: string } => {
    const now = new Date();
    let startDate: Date | null = null;
    let endDate: Date = new Date(now);

    switch (dateRange) {
      case "전체":
        return {};
      case "오늘":
        startDate = new Date(now);
        break;
      case "3일":
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 2);
        break;
      case "7일":
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 6);
        break;
      case "1개월":
        startDate = new Date(now);
        startDate.setMonth(startDate.getMonth() - 1);
        break;
      case "6개월":
        startDate = new Date(now);
        startDate.setMonth(startDate.getMonth() - 6);
        break;
      case "1년":
        startDate = new Date(now);
        startDate.setFullYear(startDate.getFullYear() - 1);
        break;
      case "직접선택":
        if (customDateFrom && customDateTo) {
          return {
            startDate: convertDateForAPI(customDateFrom, true),
            endDate: convertDateForAPI(customDateTo, false),
          };
        }
        return {};
      default:
        return {};
    }

    if (startDate) {
      // Date 객체를 직접 변환 (로컬 시간대 기준으로 YYYY-MM-DD 포맷)
      const formatLocalDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      return {
        startDate: convertDateForAPI(formatLocalDate(startDate), true),
        endDate: convertDateForAPI(formatLocalDate(endDate), false),
      };
    }

    return {};
  };

  const dateRangeFilter = getDateRange();

  // Fetch quizzes from API
  const { data, loading, refetch } = useGetAdminQuizzesQuery({
    variables: {
      search: searchQuery || dateRangeFilter.startDate || dateRangeFilter.endDate ? {
        search: searchQuery || undefined,
        startDate: dateRangeFilter.startDate,
        endDate: dateRangeFilter.endDate,
        dateField: dateFieldFilter as any,
      } : undefined,
      filter: {
        status: mapStatusToAPI(statusFilter) as any,
        type: mapTypeToAPI(typeFilter) as any,
        difficultyId: difficultyFilter || undefined,
        generationJobId: generationNumber || undefined,
      },
      pagination: {
        page: currentPage,
        limit: 10,
      },
      sort: sortField ? {
        field: sortField as any,
        order: sortOrder as any,
      } : undefined,
    },
    fetchPolicy: "cache-and-network",
  });

  // Transform API data to UI format
  const quizzes: Quiz[] = useMemo(() => {
    if (!data?.quizzes?.items) return [];

    return data.quizzes.items
      .map((quiz: any, index: number): Quiz => ({
        quizId: quiz.id,
        quizNumber: parseInt(quiz.id) || index + 1,
        status: mapQuizStatus(quiz.status),
        title: quiz.stem,
        type: mapQuizType(quiz.type),
        difficulty: quiz.difficultyLevel?.label || "-",
        points: quiz.awardedPoints,
        createdAt: quiz.createdAt,
        updatedAt: quiz.updatedAt,
        lastModifier: quiz.updatedBy || "-",
        sourceMaterialId: quiz.sourceMaterialId,
        generationJobId: quiz.generationJobId,
      }))
  }, [data]);

  const handleSearch = () => {
    refetch();
  };

  const handleCreateQuiz = () => {
    navigate("/quiz/management/create");
  };

  const handleReset = () => {
    setSearchQuery("");
    setStatusFilter("");
    setTypeFilter("");
    setDifficultyFilter("");
    setGenerationNumber("");
    setDateFieldFilter("createdAt");
    setDateRange("전체");
    setCustomDateFrom("");
    setCustomDateTo("");
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedIds(quizzes.map((quiz: Quiz) => quiz.quizId));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectQuiz = (quizId: string, selected: boolean) => {
    if (selected) {
      setSelectedIds(prev => [...prev, quizId]);
    } else {
      setSelectedIds(prev => prev.filter(id => id !== quizId));
    }
  };

  const handleQuizClick = (quiz: Quiz) => {
    navigate(`/quiz/management/${quiz.quizId}`, { state: { quiz } });
  };

  const handleSort = (key: string) => {
    // Map UI keys to API field names
    const fieldMap: { [key: string]: string } = {
      points: "AWARDED_POINTS",
      createdAt: "CREATED_AT",
      updatedAt: "UPDATED_AT",
    };

    const apiField = fieldMap[key];
    if (!apiField) return; // Only allow sortable fields

    // Three-state sorting: DESC -> ASC -> no sort
    if (sortField === apiField) {
      if (sortOrder === "DESC") {
        setSortOrder("ASC");
      } else {
        // Third click: reset to no sort
        setSortField("");
        setSortOrder("DESC");
      }
    } else {
      // First click: set field and DESC
      setSortField(apiField);
      setSortOrder("DESC");
    }
  };

  const handleActivate = async () => {
    if (selectedIds.length === 0) return;

    const confirmMessage = `선택된 ${selectedIds.length}개의 퀴즈를 활성화하시겠습니까?`;
    if (!window.confirm(confirmMessage)) return;

    try {
      await Promise.all(
        selectedIds.map((id) =>
          activateQuiz({ variables: { id } })
        )
      );
      setSelectedIds([]);
      alert(`${selectedIds.length}개의 퀴즈가 활성화되었습니다.`);
      refetch();
    } catch (error) {
      console.error("Activate failed:", error);
      alert("퀴즈 활성화에 실패했습니다.");
    }
  };

  const handleDelete = async () => {
    if (selectedIds.length === 0) return;

    const confirmMessage = `선택된 ${selectedIds.length}개의 퀴즈를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`;
    if (!window.confirm(confirmMessage)) return;

    try {
      await Promise.all(
        selectedIds.map((id) =>
          deleteQuiz({ variables: { id } })
        )
      );
      setSelectedIds([]);
      alert(`${selectedIds.length}개의 퀴즈가 삭제되었습니다.`);
      refetch();
    } catch (error) {
      console.error("Delete failed:", error);
      alert("퀴즈 삭제에 실패했습니다.");
    }
  };

  const handleViewDocument = async (quiz: Quiz) => {
    if (!quiz.sourceMaterialId) {
      alert("기반자료 정보가 없습니다.");
      return;
    }

    try {
      const { data } = await getDownloadUrl({
        variables: { id: quiz.sourceMaterialId },
      });

      const url = data?.getSourceMaterialDownloadUrl;
      if (url) {
        window.open(url, "_blank");
      } else {
        alert("다운로드 URL을 가져올 수 없습니다.");
      }
    } catch (error) {
      console.error("기반자료 다운로드 오류:", error);
      alert("기반자료를 다운로드하는 중 오류가 발생했습니다.");
    }
  };

  return (
    <PageWrapper>
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between mb-6 flex-shrink-0">
          <h1 className="text-2xl font-bold">퀴즈관리</h1>
        </div>

        <div className="mb-6 flex-shrink-0">
          <QuizSearchFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            typeFilter={typeFilter}
            onTypeChange={setTypeFilter}
            difficultyFilter={difficultyFilter}
            onDifficultyChange={setDifficultyFilter}
            difficultyOptions={difficultyOptions}
            generationNumber={generationNumber}
            onGenerationNumberChange={setGenerationNumber}
            dateFieldFilter={dateFieldFilter}
            onDateFieldFilterChange={setDateFieldFilter}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            customDateFrom={customDateFrom}
            onCustomDateFromChange={setCustomDateFrom}
            customDateTo={customDateTo}
            onCustomDateToChange={setCustomDateTo}
            onSearch={handleSearch}
            onCreateQuiz={handleCreateQuiz}
            onReset={handleReset}
            totalCount={data?.quizzes?.meta?.total}
          />
        </div>

        <div className="flex-1 min-h-0 mb-4">
          <QuizTable
            quizzes={quizzes}
            selectedIds={selectedIds}
            onSelectAll={handleSelectAll}
            onSelectQuiz={handleSelectQuiz}
            onQuizClick={handleQuizClick}
            onViewDocument={handleViewDocument}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={handleSort}
            isLoading={loading}
          />
        </div>

        <div className="flex flex-col gap-3 flex-shrink-0" style={{ minHeight: selectedIds.length > 0 ? '80px' : '40px' }}>
          <QuizPagination
            currentPage={currentPage}
            totalPages={data?.quizzes?.meta?.totalPages || 1}
            totalCount={data?.quizzes?.meta?.total || 0}
            onPageChange={setCurrentPage}
          />

          {selectedIds.length > 0 && (
            <QuizBulkActions
              selectedCount={selectedIds.length}
              onActivate={handleActivate}
              onDelete={handleDelete}
            />
          )}
        </div>
      </div>
    </PageWrapper>
  );
}